import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Eye, EyeOff, ArrowLeft, CheckCircle2, Hash, Phone, Building2, User, Mail, Chrome } from 'lucide-react';
import forkHireMark from '@/assets/forkhire-f-mark.svg';
import { useToast } from '@/hooks/use-toast';
import { INDUSTRIES } from '@/data/industries';
import { getForkHomeUrl } from '@/lib/selfHostedUrls';
import { getPortalAuthEmail, isPortalAuthNamespacingEnabled, normalizeVisibleEmail } from '@/lib/portalAuthIdentity';
import {
  protectedPasswordLogin,
  protectedPasswordRecovery,
  protectedResendVerification,
  protectedSignUp,
} from '@/lib/protectedPublicAuth';
import {
  isGoogleAuthEnabled,
} from '@/lib/googleAuth';
import { initiatePortalGoogleOAuth } from '@/lib/portalOAuthFlow';
import { useDirection } from '@/hooks/useDirection';
import {
  isForkHireEmailVerificationRequiredError,
  prepareForkHireEmailPasswordAccount,
  type ForkHireEmailProvisioningClient,
} from '@/lib/forkhireEmailProvisioning';

export default function Auth() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { dir } = useDirection();
  const { session, loading: authLoading } = useAuth();
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [crNumber, setCrNumber] = useState('');
  const [unifiedNumber, setUnifiedNumber] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [industry, setIndustry] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { toast } = useToast();
  const [emailVerified, setEmailVerified] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    // If user lands here after email verification, show a success message
    const params = new URLSearchParams(window.location.search);
    if (params.get('verified') === 'true') {
      setEmailVerified(true);
      setMode('login');
    }
  }, []);

  useEffect(() => {
    if (!authLoading && session && !loading) {
      navigate('/dashboard', { replace: true });
    }
  }, [session, authLoading, loading, navigate]);

  useEffect(() => { setStep(1); }, [mode]);

  const validateStep1 = () => {
    if (!orgName.trim()) { toast({ title: t('hire.auth.companyNameRequired'), variant: 'destructive' }); return false; }
    if (!/^\d{10}$/.test(crNumber)) { toast({ title: t('hire.auth.crNumberRequired'), variant: 'destructive' }); return false; }
    if (unifiedNumber && !/^7\d{9}$/.test(unifiedNumber)) { toast({ title: t('hire.auth.unifiedNumberInvalid'), variant: 'destructive' }); return false; }
    if (vatNumber && !/^3\d{14}$/.test(vatNumber)) { toast({ title: t('hire.auth.vatNumberInvalid'), variant: 'destructive' }); return false; }
    if (!industry) { toast({ title: t('hire.auth.industryRequired'), variant: 'destructive' }); return false; }
    return true;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast({ title: t('hire.auth.enterEmailAndPassword'), variant: 'destructive' });
      return;
    }
    setLoading(true);
    const namespacingEnabled = isPortalAuthNamespacingEnabled();
    const visibleEmail = namespacingEnabled ? normalizeVisibleEmail(email) : email.trim();
    const authEmail = namespacingEnabled ? getPortalAuthEmail('hire', visibleEmail) : visibleEmail;
    const { data, error } = await protectedPasswordLogin(supabase, {
      portal: 'hire',
      email: authEmail,
      visibleEmail,
      password,
    });
    if (error) {
      if (error.code === 'REQUEST_VERIFICATION_FAILED') {
        toast({
          title: t('hire.auth.loginFailed'),
          description: t('common.requestVerificationFailed'),
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }
      if (error.code === 'PUBLIC_AUTH_UNAVAILABLE') {
        toast({
          title: t('hire.auth.loginFailed'),
          description: t('common.serviceUnavailable'),
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }
      if (isForkHireEmailVerificationRequiredError(error)) {
        setPendingVerificationEmail(visibleEmail);
        toast({
          title: t('hire.auth.loginFailed'),
          description: t('hire.auth.verifyEmailFirst'),
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      const { data: emailExistsRpc } = await supabase.rpc('check_email_exists', { _email: visibleEmail, _portal: 'hire' });

      toast({
        title: t('hire.auth.loginFailed'),
        description: emailExistsRpc ? t('hire.auth.incorrectPassword') : t('hire.auth.noAccountFound'),
        variant: 'destructive',
      });
      setLoading(false);
      return;
    }
    if (data.user) {
      setPendingVerificationEmail('');
      const provisioning = await prepareForkHireEmailPasswordAccount(
        supabase as unknown as ForkHireEmailProvisioningClient,
        data.user,
        { visibleEmail },
      );

      if (provisioning.ok === false) {
        await supabase.auth.signOut();
        toast({
          title: t('hire.auth.loginFailed'),
          description: provisioning.message,
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      // Check if user must reset password (invited with temp password)
      if (data.user.user_metadata?.must_reset_password) {
        setLoading(false);
        navigate('/force-reset-password', { replace: true });
        return;
      }

      setLoading(false);
      navigate(provisioning.redirectTo, { replace: true });
      return;
    }
    setLoading(false);
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const namespacingEnabled = isPortalAuthNamespacingEnabled();
    const visibleEmail = namespacingEnabled ? normalizeVisibleEmail(email) : email;
    const authEmail = namespacingEnabled ? getPortalAuthEmail('hire', visibleEmail) : email;
    const { data, error } = await protectedSignUp(supabase, {
      portal: 'hire',
      email: authEmail,
      visibleEmail,
      password,
      fullName,
    });
    const signupData = data;
    if (error) {
      const alreadyRegistered = error.message.toLowerCase().includes('already registered');
      const verificationFailed = error.code === 'REQUEST_VERIFICATION_FAILED';
      toast({
        title: alreadyRegistered ? t('hire.auth.accountAlreadyExists') : t('hire.auth.signupFailed'),
        description: alreadyRegistered
          ? t('hire.auth.signInOrResetPassword')
          : verificationFailed ? t('common.requestVerificationFailed') : error.message,
        variant: 'destructive',
      });
      setLoading(false);
      return;
    }

    if (
      signupData.user &&
      !signupData.session &&
      Array.isArray(signupData.user.identities) &&
      signupData.user.identities.length === 0
    ) {
      setPendingVerificationEmail(visibleEmail);
      setMode('login');
      setPassword('');
      toast({
        title: t('hire.auth.accountAlreadyExists'),
        description: t('hire.auth.signInVerifyOrReset'),
        variant: 'destructive',
      });
      setLoading(false);
      return;
    }

    // The governed signup transport never imports credentials into the browser.
    // Account preparation begins only after the purpose-bound email link is used.
    toast({
      title: t('hire.auth.accountCreated'),
      description: t('hire.auth.verifyBeforeSignIn'),
    });
    // Reset form to login mode
    setMode('login');
    setEmail('');
    setPassword('');
    setFullName('');
    setOrgName('');
    setCrNumber('');
    setUnifiedNumber('');
    setVatNumber('');
    setIndustry('');
    setContactName('');
    setContactNumber('');
    setContactEmail('');
    setLoading(false);
  };

  const handleResendVerification = async () => {
    const rawEmail = pendingVerificationEmail || email;
    if (!rawEmail.trim()) {
      toast({ title: t('hire.auth.enterEmail'), variant: 'destructive' });
      return;
    }

    setResendLoading(true);
    const namespacingEnabled = isPortalAuthNamespacingEnabled();
    const visibleEmail = namespacingEnabled ? normalizeVisibleEmail(rawEmail) : rawEmail.trim();
    const authEmail = namespacingEnabled ? getPortalAuthEmail('hire', visibleEmail) : visibleEmail;
    const { error } = await protectedResendVerification({
      portal: 'hire',
      email: authEmail,
      visibleEmail,
    });

    if (error) {
      console.error('forkhire_verification_resend_failed', {
        reason: error.code ?? 'resend_failed',
        status: error.status,
      });
      toast({
        title: t('hire.auth.resendVerificationFailed'),
        description: t('hire.auth.tryAgainMoment'),
        variant: 'destructive',
      });
    } else {
      toast({
        title: t('hire.auth.verificationEmailSent'),
        description: t('hire.auth.verifyBeforeSignIn'),
      });
    }

    setResendLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail.trim()) {
      toast({ title: t('hire.auth.enterEmail'), variant: 'destructive' });
      return;
    }
    setResetLoading(true);
    const namespacingEnabled = isPortalAuthNamespacingEnabled();
    const visibleEmail = namespacingEnabled ? normalizeVisibleEmail(resetEmail) : resetEmail.trim();
    const authEmail = namespacingEnabled ? getPortalAuthEmail('hire', visibleEmail) : visibleEmail;
    const { error } = await protectedPasswordRecovery({
      portal: 'hire',
      email: authEmail,
      visibleEmail,
    });
    setResetLoading(false);
    if (error) {
      toast({
        title: error.code === 'REQUEST_VERIFICATION_FAILED'
          ? t('common.requestVerificationFailed')
          : error.message,
        variant: 'destructive',
      });
    } else {
      toast({ title: t('hire.auth.resetLinkSentToEmail') });
      setForgotOpen(false);
      setResetEmail('');
    }
  };

  const handleGoogleSignIn = async () => {
    if (!isGoogleAuthEnabled('hire')) return;

    setGoogleLoading(true);
    const result = await initiatePortalGoogleOAuth(supabase, {
      portal: 'hire',
      continuation: '/dashboard',
    });

    if (result.ok === false) {
      console.error('forkhire_google_oauth_initiation_failed', { reason: result.reason });
      toast({
        title: t('hire.auth.googleSignInFailed'),
        variant: 'destructive',
      });
      setGoogleLoading(false);
    }
  };

  const openForgotDialog = () => {
    setResetEmail(email);
    setForgotOpen(true);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" dir={dir}>
      <div className="w-full max-w-md">

        {emailVerified && (
          <div className="flex items-center gap-3 p-4 rounded-xl bg-success/10 border border-success/20 mb-4">
            <CheckCircle2 className="w-5 h-5 text-success shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground">{t('hire.auth.emailVerified')}</p>
              <p className="text-xs text-muted-foreground">{t('hire.auth.emailVerifiedDesc')}</p>
            </div>
          </div>
        )}

        <Card className="w-full rounded-xl shadow-card">
          <CardHeader className="text-center pb-2 pt-8">
            <div className="flex justify-start mb-4">
              <Button
                size="sm"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg"
                onClick={() => { window.location.href = getForkHomeUrl(); }}
              >
                <ArrowLeft className="w-4 h-4 me-1 rtl-flip" />
                {t('seek.auth.home')}
              </Button>
            </div>
            <img src={forkHireMark} alt="ForkHire" className="w-10 h-auto mx-auto mb-4" />
            <h1 className="text-2xl font-bold font-display text-foreground">
              {mode === 'login' ? t('seek.auth.welcomeBack') : step === 1 ? t('hire.auth.registerCompany') : t('hire.auth.createAccount')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {mode === 'login' ? t('hire.auth.signIn') : step === 1 ? t('hire.auth.stepCompanyDetails') : t('hire.auth.stepAccountContact')}
            </p>
          </CardHeader>
          <CardContent className="px-8 pb-8">
            {/* Login Form */}
            {mode === 'login' && (
              <>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="login-email">{t('hire.auth.email')}</Label>
                    <Input
                      id="login-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      required
                      className="h-11"
                      dir="ltr"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password">{t('hire.auth.password')}</Label>
                      <button
                        type="button"
                        onClick={openForgotDialog}
                        className="text-xs text-primary hover:underline"
                      >
                        {t('hire.auth.forgotPassword')}
                      </button>
                    </div>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                        minLength={6}
                        className="h-11 pe-11"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-11 rounded-lg mt-2 bg-primary hover:bg-primary/90 text-primary-foreground"
                    disabled={loading || googleLoading}
                  >
                    {loading ? t('hire.auth.signingIn') : t('hire.auth.signInBtn')}
                  </Button>
                </form>
                {pendingVerificationEmail && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-11 rounded-lg mt-3"
                    onClick={handleResendVerification}
                    disabled={loading || googleLoading || resendLoading}
                  >
                    {resendLoading ? t('hire.auth.resending') : t('hire.auth.resendVerificationEmail')}
                  </Button>
                )}
                {isGoogleAuthEnabled('hire') && (
                  <div className="mt-5 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="h-px flex-1 bg-border" />
                      <span className="text-xs text-muted-foreground">{t('hire.auth.or')}</span>
                      <div className="h-px flex-1 bg-border" />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-11 rounded-lg"
                      onClick={handleGoogleSignIn}
                      disabled={loading || googleLoading}
                    >
                      <Chrome className="w-4 h-4 me-2" />
                      {googleLoading ? t('hire.auth.connecting') : t('hire.auth.continueWithGoogle')}
                    </Button>
                  </div>
                )}
                <p className="text-sm text-center text-muted-foreground mt-5">
                  {t('hire.auth.noAccountPrefix')}{' '}
                  <button onClick={() => setMode('signup')} className="text-primary font-medium hover:underline">
                    {t('seek.auth.signUpLink')}
                  </button>
                </p>
              </>
            )}

            {/* Signup Step 1: Company Legal Info */}
            {mode === 'signup' && step === 1 && (
              <>
                <div className="flex items-center gap-2 mb-5">
                  <div className="flex-1 h-1.5 rounded-full bg-primary" />
                  <div className="flex-1 h-1.5 rounded-full bg-muted" />
                </div>

                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>{t('hire.auth.companyName')} <span className="text-destructive">*</span></Label>
                    <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder={t('hire.auth.companyNamePlaceholder')} className="h-11" required />
                  </div>

                  <div className="space-y-1.5">
                    <Label>{t('hire.auth.crNumber')} <span className="text-destructive">*</span></Label>
                    <Input value={crNumber} onChange={(e) => setCrNumber(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder={t('hire.auth.crNumberPlaceholder')} className="h-11" required maxLength={10} dir="ltr" />
                    <p className="text-xs text-muted-foreground">{t('hire.auth.crNumberHint')}</p>
                  </div>

                  <div className="space-y-1.5">
                    <Label>{t('hire.auth.unifiedNumber')}</Label>
                    <Input value={unifiedNumber} onChange={(e) => setUnifiedNumber(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="700XXXXXXX" className="h-11" maxLength={10} dir="ltr" />
                    <p className="text-xs text-muted-foreground">{t('hire.auth.unifiedNumberHint')}</p>
                  </div>

                  <div className="space-y-1.5">
                    <Label>{t('hire.auth.vatNumber')}</Label>
                    <Input value={vatNumber} onChange={(e) => setVatNumber(e.target.value.replace(/\D/g, '').slice(0, 15))} placeholder="300XXXXXXXXXXXX" className="h-11" maxLength={15} dir="ltr" />
                    <p className="text-xs text-muted-foreground">{t('hire.auth.vatNumberHint')}</p>
                  </div>

                  <div className="space-y-1.5">
                    <Label>{t('hire.auth.industry')} <span className="text-destructive">*</span></Label>
                    <select
                      value={industry}
                      onChange={(e) => setIndustry(e.target.value)}
                      className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="">{t('hire.auth.selectIndustry')}</option>
                      {INDUSTRIES.map((ind) => (
                        <option key={ind} value={ind}>{ind}</option>
                      ))}
                    </select>
                  </div>

                  <Button
                    type="button"
                    className="w-full h-11 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground"
                    onClick={() => { if (validateStep1()) setStep(2); }}
                  >
                    {t('hire.auth.continueBtn')}
                  </Button>
                </div>

                <p className="text-sm text-center text-muted-foreground mt-5">
                  {t('hire.auth.alreadyHaveAccountPrefix')}{' '}
                  <button onClick={() => setMode('login')} className="text-primary font-medium hover:underline">
                    {t('seek.auth.signInLink')}
                  </button>
                </p>
              </>
            )}

            {/* Signup Step 2: Account & Contact Info */}
            {mode === 'signup' && step === 2 && (
              <>
                <div className="flex items-center gap-2 mb-5">
                  <div className="flex-1 h-1.5 rounded-full bg-primary" />
                  <div className="flex-1 h-1.5 rounded-full bg-primary" />
                </div>

                <form onSubmit={handleSignup} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>{t('hire.auth.fullName')} <span className="text-destructive">*</span></Label>
                    <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder={t('hire.auth.fullNamePlaceholder')} className="h-11" required />
                  </div>

                  <div className="space-y-1.5">
                    <Label>{t('hire.auth.email')} <span className="text-destructive">*</span></Label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="h-11" required dir="ltr" />
                  </div>

                  <div className="space-y-1.5">
                    <Label>{t('hire.auth.password')} <span className="text-destructive">*</span></Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t('hire.auth.passwordPlaceholder')}
                        className="h-11 pe-11"
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="border-t border-border pt-4 mt-2">
                    <p className="text-sm font-medium text-foreground mb-3">
                      {t('hire.auth.contactPerson')} <span className="text-xs text-muted-foreground font-normal">({t('hire.auth.receivesSystemNotifications')})</span>
                    </p>
                    <div className="space-y-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">{t('hire.auth.contactName')}</Label>
                        <Input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder={t('hire.auth.defaultsToYourName')} className="h-11" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">{t('hire.auth.contactNumber')}</Label>
                        <Input value={contactNumber} onChange={(e) => setContactNumber(e.target.value)} placeholder="+966 5XXXXXXXX" className="h-11" dir="ltr" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">{t('hire.auth.contactEmail')}</Label>
                        <Input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder={t('hire.auth.defaultsToYourEmail')} className="h-11" dir="ltr" />
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground"
                    disabled={loading}
                  >
                    {loading ? t('hire.auth.creatingAccount') : t('hire.auth.createAccountBtn')}
                  </Button>
                </form>

                <div className="mt-5 flex justify-between items-center">
                  <button onClick={() => setStep(1)} className="text-sm text-primary hover:underline inline-flex items-center gap-1.5">
                    <ArrowLeft className="w-4 h-4 rtl-flip" /> {t('common.back')}
                  </button>
                  <button onClick={() => setMode('login')} className="text-sm text-primary hover:underline">
                    {t('hire.auth.alreadyHaveAccountPrefix')}
                  </button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Forgot Password Dialog — matches ForkSeek style */}
        <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
          <DialogContent className="sm:max-w-sm rounded-xl" dir={dir}>
            <DialogHeader>
              <DialogTitle className="text-lg font-display">{t('seek.auth.resetPasswordTitle')}</DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                {t('seek.auth.resetPasswordDesc')}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleForgotPassword} className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <Label htmlFor="reset-email">{t('hire.auth.email')}</Label>
                <Input
                  id="reset-email"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  className="h-11"
                  dir="ltr"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-11 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground"
                disabled={resetLoading}
              >
                {resetLoading ? t('seek.auth.sendingReset') : t('hire.auth.sendResetLink')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
